import { Point } from '../../../core/models/point';
import { Figure } from './figure';
import * as jsts from 'jsts';
import { konvaSettings } from '../konvaSettings';
import { Utils } from '../../../core/services/utils.service';
import { Equipment } from '../equipment';
import { Bounding } from '../../../core/models/bounding';
import { Range } from '../../../core/models/range';
import { UnitConverter } from '../../../core/utils/unitConverter';

const Coordinate = jsts.geom.Coordinate,
	GeometryFactory = jsts.geom.GeometryFactory,
	geometryFactory: jsts.geom.GeometryFactory = new GeometryFactory();

export class Rectangle implements Figure {
	type: string = 'rectangle';
	begin: Point;
	width: number;
	height: number;
	depth: number;
	angle: number = 0;
	frontAngle: number = 0;
	representation: string = undefined;
	rotationPoint: { x: number, y: number };
	wallWidth: number; // boxes
	priority: 'normal';
	strokeColor: string = konvaSettings.fillDarkColor;
	fillColor: string = konvaSettings.fillDarkColor;
	empty: {
		front: boolean,   // відмальовка порожнистого обєкта
		top: boolean
	};
	distanceBetweenHooks: number;
	distanceBetweenRowsHooks: number;
	firstHookTopDistance: number;
	firstHookLeftDistance: number;
	distanceBetweenHoles: number;
	distanceBetweenRowsHoles: number;
	firstHoleTopDistance: number;
	firstHoleLeftDistance: number;
	hooksAutolayout: boolean = false;
	prevObjectHash: string;
	cache: any = Rectangle.returnClearCacheObject();

	get square(): number {
		return Utils.round(this.width * this.depth);
	}

	constructor(params: any) {
		this.wallWidth = UnitConverter.convertAndRoundToSaveValue(.01);
		this.distanceBetweenHooks = UnitConverter.convertAndRoundToSaveValue(.1);
		this.distanceBetweenRowsHooks = UnitConverter.convertAndRoundToSaveValue(.1);
		this.firstHookTopDistance = UnitConverter.convertAndRoundToSaveValue(.04);
		this.firstHookLeftDistance = UnitConverter.convertAndRoundToSaveValue(.04);
		this.distanceBetweenHoles = UnitConverter.convertAndRoundToSaveValue(.025, 'distance', 'inches', 3);
		this.distanceBetweenRowsHoles = UnitConverter.convertAndRoundToSaveValue(.025, 'distance', 'inches', 3);
		this.firstHoleTopDistance = UnitConverter.convertAndRoundToSaveValue(.025, 'distance', 'inches', 3);
		this.firstHoleLeftDistance = UnitConverter.convertAndRoundToSaveValue(.025, 'distance', 'inches', 3);

		const {
				empty,
				begin,
				height,
				width,
				depth,
				angle,
				fillColor,
				priority,
				frontAngle,
				rotationPoint,
				wallWidth,
				representation,
				distanceBetweenHooks,
				distanceBetweenRowsHooks,
				firstHookLeftDistance,
				firstHookTopDistance,
				hooksAutolayout,
				distanceBetweenHoles,
				distanceBetweenRowsHoles,
				firstHoleTopDistance,
				firstHoleLeftDistance
			} = params,
			validatedParams = this.validate({
				empty,
				begin,
				height,
				width,
				depth,
				angle,
				fillColor,
				priority,
				frontAngle,
				rotationPoint,
				wallWidth,
				representation,
				distanceBetweenHooks,
				distanceBetweenRowsHooks,
				firstHookLeftDistance,
				firstHookTopDistance,
				hooksAutolayout,
				distanceBetweenHoles,
				distanceBetweenRowsHoles,
				firstHoleTopDistance,
				firstHoleLeftDistance
			});

		Object.assign(this, validatedParams);

		if (!rotationPoint) {
			this.rotationPoint = {
				x: this.begin.z,
				y: Utils.round(this.begin.y + this.height)
			};
		}
	}

	set beginSetter(value: Point) {
		this.begin = {
			x: Utils.round(value.x),
			y: Utils.round(value.y),
			z: Utils.round(value.z),
		};

		this.cache = Rectangle.returnClearCacheObject();
	}

	get coordinates(): Array<Point> {
		return [
			{ x: this.begin.x, y: this.begin.y, z: Utils.round(this.begin.z + this.depth) },
			{ x: Utils.round(this.begin.x + this.width), y: this.begin.y, z: this.begin.z },
			{
				x: Utils.round(this.begin.x + this.width),
				y: Utils.round(this.begin.y + this.height),
				z: Utils.round(this.begin.z + this.depth)
			},
			{ x: this.begin.x, y: Utils.round(this.begin.y + this.height), z: this.begin.z },
			{ x: this.begin.x, y: this.begin.y, z: Utils.round(this.begin.z + this.depth) }
		];
	}

	get savedParams() {
		const {
			empty,
			begin,
			height,
			width,
			depth,
			angle,
			fillColor,
			priority,
			frontAngle,
			rotationPoint,
			wallWidth,
			representation,
			distanceBetweenHooks,
			distanceBetweenRowsHooks,
			firstHookLeftDistance,
			firstHookTopDistance,
			hooksAutolayout,
			distanceBetweenHoles,
			distanceBetweenRowsHoles,
			firstHoleTopDistance,
			firstHoleLeftDistance,
			type
		} = this;
		return {
			empty,
			begin,
			height,
			width,
			depth,
			angle,
			fillColor,
			priority,
			frontAngle,
			rotationPoint,
			wallWidth,
			representation,
			distanceBetweenHooks,
			distanceBetweenRowsHooks,
			firstHookLeftDistance,
			firstHookTopDistance,
			hooksAutolayout,
			distanceBetweenHoles,
			distanceBetweenRowsHoles,
			firstHoleTopDistance,
			firstHoleLeftDistance,
			type
		};
	}

	static returnClearCacheObject() {
		return {
			front: {},
			top: {},
			side: {}
		};
	}

	private getCachedValue(params) {
		const { calculate_func, key, representation = this.representation } = params;

		if (!this.cache[representation]) {
			this.cache[representation] = {};
		}

		if (this.prevObjectHash) {
			const { begin } = JSON.parse(this.prevObjectHash);

			if (!Utils.equalObjects(begin, this.begin)) {
				delete this.cache[representation][key];
			}
		}

		if (!this.cache[representation][key]) {
			if (typeof calculate_func == 'function') {
				this.cache[representation][key] = calculate_func();
			}
		}

		return this.cache[representation][key];
	}

	update(params: any): void {
		Object.keys(params).forEach((param: string) => {
			if (this[param] != undefined) this[param] = params[param];
		});

		this.cache = Rectangle.returnClearCacheObject();
	}

	updateRotationPoint(instrument, element) {
		this.rotationPoint = { x: this.begin.z, y: Utils.round(this.begin.y + this.height) };

		instrument.allElements.forEach(el => {
			if (!el.getDepends().includes(element)) return;
			el.shapes[0].rotationPoint = Object.assign({}, this.rotationPoint);
			el.shapes[0].frontAngle = this.frontAngle;
		});
	}

	copy(): Rectangle {
		const {
			empty,
			begin,
			height,
			width,
			depth,
			angle,
			fillColor,
			priority,
			frontAngle,
			rotationPoint,
			wallWidth,
			representation,
			distanceBetweenHooks,
			distanceBetweenRowsHooks,
			firstHookLeftDistance,
			firstHookTopDistance,
			hooksAutolayout,
			distanceBetweenHoles,
			distanceBetweenRowsHoles,
			firstHoleTopDistance,
			firstHoleLeftDistance
		} = this;

		return new Rectangle({
			height,
			width,
			depth,
			angle,
			fillColor,
			priority,
			frontAngle,
			wallWidth,
			representation,
			empty: Object.assign({}, empty),
			begin: Object.assign({}, begin),
			rotationPoint: Object.assign({}, rotationPoint),
			distanceBetweenHooks,
			distanceBetweenRowsHooks,
			firstHookLeftDistance,
			firstHookTopDistance,
			hooksAutolayout,
			distanceBetweenHoles,
			distanceBetweenRowsHoles,
			firstHoleTopDistance,
			firstHoleLeftDistance
		});
	}

	getBoundingX(usable_area: boolean = false): Bounding {
		return this.getCachedValue({
			key: 'getBoundingX',
			calculate_func: () => {
				const coordinates = this.getCoordinates(),
					xList = [...coordinates.bottom.map(p => p.x), ...coordinates.top.map(p => p.x)];

				return {
					min: Utils.round(Math.min(...xList)),
					max: Utils.round(Math.max(...xList))
				};
			}
		});
	}

	getBoundingY(): Bounding {
		return this.getCachedValue({
			key: 'getBoundingY',
			calculate_func: () => {
				const coordinates = this.getCoordinates(),
					yList = [
						...coordinates.bottom.map(p => p.y),
						...coordinates.top.map(p => p.y)
					];

				return {
					min: Utils.round(Math.min(...yList)),
					max: Utils.round(Math.max(...yList))
				};
			}
		});
	}

	getBoundingZ(): Bounding {
		return this.getCachedValue({
			key: 'getBoundingZ',
			calculate_func: () => {
				const coordinates = this.getCoordinates(),
					zList = [...coordinates.bottom.map(p => p.z), ...coordinates.top.map(p => p.z)];

				return {
					min: Utils.round(Math.min(...zList)),
					max: Utils.round(Math.max(...zList))
				};
			}
		});
	}

	getAbsoluteBoundingZ(): Bounding {
		const bottom: Array<Point> = [
				{ x: this.begin.x, y: this.begin.y, z: this.begin.z },
				{ x: this.begin.x + this.width, y: this.begin.y, z: this.begin.z },
				{ x: this.begin.x + this.width, y: this.begin.y, z: this.begin.z + this.depth },
				{ x: this.begin.x, y: this.begin.y, z: this.begin.z + this.depth }
			],
			top: Array<Point> = bottom.map((p: Point) => {
				return { x: p.x, y: p.y + this.height, z: p.z };
			});

		const coordinates = { bottom, top },
			zList = [...coordinates.bottom.map(p => p.z), ...coordinates.top.map(p => p.z)];

		return {
			min: Utils.round(Math.min(...zList)),
			max: Utils.round(Math.max(...zList))
		};
	}

	getBoundingZBetween(compareSize: Bounding): Bounding {
		const bottom = this.getCoordinates().bottom.map(p => ({ x: Utils.round(p.x), z: Utils.round(p.z) })),

			sizesPolygon = geometryFactory.createPolygon([
				new Coordinate(compareSize.min, 0),
				new Coordinate(compareSize.max, 0),
				new Coordinate(compareSize.max, 200),
				new Coordinate(compareSize.min, 200),
				new Coordinate(compareSize.min, 0)
			]),

			shapePolygon = geometryFactory.createPolygon([
				new Coordinate(bottom[0].x, bottom[0].z),
				new Coordinate(bottom[1].x, bottom[1].z),
				new Coordinate(bottom[2].x, bottom[2].z),
				new Coordinate(bottom[3].x, bottom[3].z),
				new Coordinate(bottom[0].x, bottom[0].z)
			]);

		if (!sizesPolygon.intersects(shapePolygon)) {
			return {
				min: 0,
				max: 0
			};
		}

		const intersection = sizesPolygon.intersection(shapePolygon),
			coordinates = intersection.getCoordinates(),
			zList = [...coordinates.map(p => p.y)];

		return {
			min: Utils.round(Math.min(...zList)),
			max: Utils.round(Math.max(...zList))
		};
	}

	inRange(range: Range): boolean {
		const xBounding: Bounding = this.getBoundingX();

		return !(range.to <= xBounding.min || range.from >= xBounding.max);
	}

	isPointOn(x?: number, y?: number, z?: number): boolean {
		if (x != null) {
			const xBounding = this.getBoundingX();
			x = Utils.round(x);
			if (x < xBounding.min || x > xBounding.max) return false;
		}
		if (y != null) {
			const yBounding = this.getBoundingY();
			y = Utils.round(y);
			if (y < yBounding.min || y > yBounding.max) return false;
		}
		if (z != null) {
			const zBounding = this.getAbsoluteBoundingZ();
			z = Utils.round(z);
			if (z < zBounding.min || z > zBounding.max) return false;
		}

		return true;
	}

	isEqual(hash: string): boolean {
		return this.prevObjectHash == hash;
	}

	getObjectHash(kwargs: any) {
		const {
			angle,
			begin,
			depth,
			firstHookLeftDistance,
			firstHookTopDistance,
			firstHoleTopDistance,
			firstHoleLeftDistance,
			frontAngle,
			height,
			hooksAutolayout,
			rotationPoint,
			strokeColor,
			type,
			wallWidth,
			width,
			representation
		} = this;

		return JSON.stringify({
			angle,
			begin,
			depth,
			firstHookLeftDistance,
			firstHookTopDistance,
			frontAngle,
			height,
			hooksAutolayout,
			rotationPoint,
			strokeColor,
			type,
			wallWidth,
			width,
			representation,
			...kwargs
		});
	}

	drag(x?: number, y?: number, z?: number): void {
		if (x) this.begin.x = Utils.round(this.begin.x + x);
		if (y) this.begin.y = Utils.round(this.begin.y + y);
		if (z) this.begin.z = Utils.round(this.begin.z + z);

		this.cache = Rectangle.returnClearCacheObject();
	}

	getCoordinates() {
		let bottom: Array<Point> = [
			{ x: this.begin.x, y: this.begin.y, z: this.begin.z },
			{ x: Utils.round(this.begin.x + this.width), y: this.begin.y, z: this.begin.z },
			{
				x: Utils.round(this.begin.x + this.width),
				y: this.begin.y,
				z: Utils.round(this.begin.z + this.depth)
			},
			{ x: this.begin.x, y: this.begin.y, z: Utils.round(this.begin.z + this.depth) }
		];
		let top: Array<Point> = bottom.map(p => {
			const { x, y, z } = p;

			return { x, y: Utils.round(y + this.height), z };
		});

		if (this.angle != 0) {
			bottom = bottom.map(p => this.rotatePoint(p));

			return { bottom, top };
		}

		if (this.frontAngle != 0 && this.representation !== 'top') {
			bottom = bottom.map(p => this.rotatePointByFrontAngle(p));
			top = top.map(p => this.rotatePointByFrontAngle(p));
		}

		return { bottom, top };
	}

	rotatePointByFrontAngle(p: Point): Point {
		const rotationX = this.rotationPoint.x,
			rotationY = this.rotationPoint.y,
			angle = -this.frontAngle * Math.PI / 180;

		return {
			x: p.x,
			z: Utils.round((p.z - rotationX) * Math.cos(angle) - (p.y - rotationY) * Math.sin(angle) + rotationX),
			y: Utils.round((p.z - rotationX) * Math.sin(angle) + (p.y - rotationY) * Math.cos(angle) + rotationY)
		};
	}

	rotatePoint(p: Point): Point {
		const rotationX = this.begin.x,
			rotationY = this.begin.z,
			angle = this.angle * Math.PI / 180;

		return {
			x: Utils.round((p.x - rotationX) * Math.cos(angle) - (p.z - rotationY) * Math.sin(angle) + rotationX),
			z: Utils.round((p.x - rotationX) * Math.sin(angle) + (p.z - rotationY) * Math.cos(angle) + rotationY),
			y: p.y
		};
	}

	getLines(representation: string, instrument: Equipment): Array<Array<number>> {
		return this.getCachedValue({
			key: 'getLines',
			calculate_func: () => {
				switch (representation) {
					case 'front': {
						const h = instrument.Height,
							xBoundings = this.getBoundingX(),
							yBoundings = this.getBoundingY();

						return [
							[xBoundings.min, h - yBoundings.max, xBoundings.max, h - yBoundings.max],
							[xBoundings.max, h - yBoundings.max, xBoundings.max, h - yBoundings.min],
							[xBoundings.max, h - yBoundings.min, xBoundings.min, h - yBoundings.min],
							[xBoundings.min, h - yBoundings.min, xBoundings.min, h - yBoundings.max]
						];
					}
					case 'top': {
						const coordinates = this.getCoordinates().top;

						return [
							[coordinates[0].x, coordinates[0].z, coordinates[1].x, coordinates[1].z],
							[coordinates[1].x, coordinates[1].z, coordinates[2].x, coordinates[2].z],
							[coordinates[2].x, coordinates[2].z, coordinates[3].x, coordinates[3].z],
							[coordinates[3].x, coordinates[3].z, coordinates[0].x, coordinates[0].z]
						];
					}
					case 'side': {
						const h = instrument.Height,
							zBoundings = this.getBoundingZ(),
							yBoundings = this.getBoundingY();

						return [
							[zBoundings.min, h - yBoundings.max, zBoundings.max, h - yBoundings.max],
							[zBoundings.max, h - yBoundings.max, zBoundings.max, h - yBoundings.min],
							[zBoundings.max, h - yBoundings.min, zBoundings.min, h - yBoundings.min],
							[zBoundings.min, h - yBoundings.min, zBoundings.min, h - yBoundings.max]
						];
					}
				}
			},
			representation
		});
	}

	private validate(params: any) {
		const validatedParams = {};

		for (let key in params) {
			if (params[key] != null) {
				validatedParams[key] = params[key];
			}
		}

		return validatedParams;
	}
}